<script>
import moment from "moment";
import Swal from "sweetalert2";

import Portal from "../../layouts/portal";
import AccesoComercio from "@/components/portal-acceso-comercio";
import { clienteComputed, negocioComputed } from "@/state/helpers";
import { misVacantes, guardarVacante, eliminarVacante, candidatosDe } from "@/services/negocio";
import { parseFechaFlexible } from "@/services/usuarios";

const VACIO = {
  tituloPuesto: "",
  nombreEmpresa: "",
  departamento: "",
  rangoSalarial: "",
  tipoContrato: "",
  requisitosIndispensables: "",
  beneficios: "",
};

export default {
  page: { title: "Mis vacantes" },
  components: { Portal, AccesoComercio },
  data() {
    return {
      vacantes: [],
      cargando: true,
      error: null,
      mostrarFormulario: false,
      guardando: false,
      editandoId: null,
      tituloOriginal: "",
      formulario: { ...VACIO },
      // Candidatos por vacante: { [idVacante]: { cargando, lista } }
      candidatos: {},
      expandida: null,
    };
  },
  computed: {
    ...clienteComputed,
    ...negocioComputed,
    /**
     * Cambiar el título desliga la vacante de sus postulaciones, porque la app
     * relaciona candidatos por título de puesto y no por identificador.
     */
    avisoDeTitulo() {
      if (!this.editandoId || !this.tituloOriginal) return null;
      if (this.formulario.tituloPuesto.trim() === this.tituloOriginal) return null;
      return "Si cambias el título del puesto, las postulaciones que ya recibiste dejarán de aparecer en esta vacante.";
    },
  },
  methods: {
    publicada(vacante) {
      const fecha = parseFechaFlexible(vacante.fechaPublicacion);
      return fecha ? moment(fecha).format("DD/MM/YYYY") : "—";
    },

    nueva() {
      this.formulario = { ...VACIO, nombreEmpresa: this.nombreNegocio };
      this.editandoId = null;
      this.tituloOriginal = "";
      this.error = null;
      this.mostrarFormulario = true;
    },

    editar(vacante) {
      this.formulario = Object.keys(VACIO).reduce((acc, clave) => {
        acc[clave] = vacante[clave] || "";
        return acc;
      }, {});
      if (!this.formulario.nombreEmpresa) this.formulario.nombreEmpresa = this.nombreNegocio;
      this.editandoId = vacante.id;
      this.tituloOriginal = vacante.tituloPuesto || "";
      this.error = null;
      this.mostrarFormulario = true;
    },

    async guardar() {
      const f = this.formulario;
      if (!f.tituloPuesto.trim() || !f.nombreEmpresa.trim()) {
        this.error = "El puesto y el nombre de la empresa son obligatorios.";
        return;
      }

      this.guardando = true;
      this.error = null;

      try {
        const datos = Object.keys(VACIO).reduce((acc, clave) => {
          acc[clave] = String(f[clave] || "").trim();
          return acc;
        }, {});

        await guardarVacante(datos, this.editandoId);
        this.mostrarFormulario = false;
        await this.cargar();
        Swal.fire("Listo", "Tu vacante se publicó.", "success");
      } catch (err) {
        console.error("[portal] error al guardar la vacante:", err);
        this.error = "No pudimos guardar la vacante. Inténtalo de nuevo.";
      } finally {
        this.guardando = false;
      }
    },

    async borrar(vacante) {
      const resultado = await Swal.fire({
        title: `¿Eliminar la vacante de ${vacante.tituloPuesto}?`,
        text: "Esta acción no se puede deshacer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!resultado.isConfirmed) return;

      try {
        await eliminarVacante(vacante.id);
        await this.cargar();
        Swal.fire("Eliminada", "La vacante ya no aparece en Recnet.", "success");
      } catch (err) {
        console.error("[portal] error al eliminar la vacante:", err);
        Swal.fire("Error", "No pudimos eliminar la vacante.", "error");
      }
    },

    async alternarCandidatos(vacante) {
      if (this.expandida === vacante.id) {
        this.expandida = null;
        return;
      }
      this.expandida = vacante.id;

      if (this.candidatos[vacante.id]) return;

      this.$set(this.candidatos, vacante.id, { cargando: true, lista: [] });
      try {
        const lista = await candidatosDe(vacante);
        this.$set(this.candidatos, vacante.id, { cargando: false, lista });
      } catch (err) {
        console.error("[portal] error al cargar candidatos:", err);
        this.$set(this.candidatos, vacante.id, { cargando: false, lista: [], error: true });
      }
    },

    async cargar() {
      this.cargando = true;
      try {
        this.vacantes = await misVacantes(this.perfilCliente.email);
        // Las postulaciones se releen la próxima vez que se abran: pudieron
        // cambiar mientras se editaba.
        this.candidatos = {};
      } catch (err) {
        console.error("[portal] error al cargar tus vacantes:", err);
        this.error = "No pudimos cargar tus vacantes.";
      } finally {
        this.cargando = false;
      }
    },
  },
  async mounted() {
    await this.$store.dispatch("negocio/cargar");
    if (this.perfilCliente) await this.cargar();
    else this.cargando = false;
  },
};
</script>

<template>
  <Portal>
    <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
      <div>
        <h1 class="h3 mb-1">Mis vacantes</h1>
        <p class="text-muted mb-0">Las oportunidades que ofreces a la red.</p>
      </div>
      <b-button v-if="esComercio && !mostrarFormulario" variant="primary" @click="nueva">
        <i class="mdi mdi-plus"></i> Nueva vacante
      </b-button>
    </div>

    <AccesoComercio>
      <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

      <!-- Formulario -->
      <div v-if="mostrarFormulario" class="vacante-card mb-4">
        <h2 class="h5 mb-4">{{ editandoId ? 'Editar vacante' : 'Nueva vacante' }}</h2>

        <b-alert :show="!!avisoDeTitulo" variant="warning">{{ avisoDeTitulo }}</b-alert>

        <b-row>
          <b-col md="6">
            <b-form-group label="Puesto *">
              <b-form-input v-model="formulario.tituloPuesto" placeholder="Auxiliar de ventas" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Empresa *">
              <b-form-input v-model="formulario.nombreEmpresa" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Departamento">
              <b-form-input v-model="formulario.departamento" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Rango salarial">
              <b-form-input v-model="formulario.rangoSalarial" placeholder="$8,000 - $12,000" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Tipo de contrato">
              <b-form-input v-model="formulario.tipoContrato" placeholder="Tiempo completo" />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Requisitos indispensables">
              <b-form-textarea v-model="formulario.requisitosIndispensables" rows="3" />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Beneficios">
              <b-form-textarea v-model="formulario.beneficios" rows="3" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="mostrarFormulario = false">Cancelar</b-button>
          <b-button variant="primary" :disabled="guardando" @click="guardar">
            {{ guardando ? 'Guardando…' : 'Publicar' }}
          </b-button>
        </div>
      </div>

      <!-- Listado -->
      <p v-if="cargando" class="text-muted">Cargando tus vacantes…</p>
      <p v-else-if="vacantes.length === 0" class="text-muted">
        Todavía no has publicado ninguna vacante.
      </p>

      <div v-else>
        <div v-for="vacante in vacantes" :key="vacante.id" class="vacante-card mb-3">
          <div class="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h3 class="h6 mb-1">{{ vacante.tituloPuesto }}</h3>
              <p class="text-muted small mb-1">
                {{ vacante.departamento || 'Sin departamento' }}
                <span v-if="vacante.tipoContrato"> · {{ vacante.tipoContrato }}</span>
                <span v-if="vacante.rangoSalarial"> · {{ vacante.rangoSalarial }}</span>
              </p>
              <p class="text-muted small mb-0">Publicada el {{ publicada(vacante) }}</p>
            </div>

            <div class="mt-2 mt-md-0">
              <b-button size="sm" variant="outline-secondary" class="mr-1"
                @click="alternarCandidatos(vacante)">
                <i class="mdi mdi-account-multiple"></i>
                {{ expandida === vacante.id ? 'Ocultar' : 'Candidatos' }}
              </b-button>
              <b-button size="sm" variant="outline-primary" class="mr-1" @click="editar(vacante)">
                <i class="mdi mdi-pencil"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="borrar(vacante)">
                <i class="mdi mdi-delete"></i>
              </b-button>
            </div>
          </div>

          <div v-if="expandida === vacante.id" class="candidatos mt-3">
            <p v-if="candidatos[vacante.id] && candidatos[vacante.id].cargando" class="text-muted small mb-0">
              Cargando postulaciones…
            </p>
            <p v-else-if="candidatos[vacante.id] && candidatos[vacante.id].error"
              class="text-danger small mb-0">
              No pudimos cargar las postulaciones.
            </p>
            <p v-else-if="candidatos[vacante.id] && candidatos[vacante.id].lista.length === 0"
              class="text-muted small mb-0">
              Todavía nadie se ha postulado a esta vacante.
            </p>
            <b-table v-else-if="candidatos[vacante.id]" small responsive hover
              :items="candidatos[vacante.id].lista" :fields="[
                { key: 'nombre', label: 'Nombre' },
                { key: 'email', label: 'Correo' },
                { key: 'telefono', label: 'Teléfono' },
                { key: 'pdfUrl', label: 'CV' },
              ]" class="mb-0">
              <template #cell(nombre)="{ item }">{{ item.nombre || '—' }}</template>
              <template #cell(telefono)="{ item }">{{ item.telefono || '—' }}</template>
              <template #cell(pdfUrl)="{ item }">
                <a v-if="item.pdfUrl" :href="item.pdfUrl" target="_blank" rel="noopener noreferrer">
                  Ver CV
                </a>
                <span v-else class="text-muted">Sin CV</span>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </AccesoComercio>
  </Portal>
</template>

<style scoped>
.vacante-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
}

.candidatos {
  border-top: 1px solid #f1f1f4;
  padding-top: 16px;
}
</style>
